/**
 * InboxTagsSection — Settings → Inbox Tags. Manage the conversation-tag catalog:
 * rename, recolor, archive, and add. (Creating a tag inline from a thread also
 * works; this is the full manager.)
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Archive, ArchiveRestore, Tag as TagIcon, MailX, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { InfoTip } from "@/components/InfoTip";
import { TAG_COLOR_KEYS, colorOf } from "@/features/inbox/tags";

interface AdminTag {
  id: string;
  name: string;
  color: string;
  sort: number;
  archived: boolean;
}

function useAllTags() {
  return useQuery({
    queryKey: ["conv_tags_admin"],
    queryFn: async (): Promise<AdminTag[]> => {
      const { data, error } = await supabase.from("conversation_tag").select("*").order("sort");
      if (error) throw error;
      return (data ?? []) as AdminTag[];
    },
  });
}

export function InboxTagsSection() {
  const canManage = useCan("settings.messages");
  const qc = useQueryClient();
  const { data, isLoading } = useAllTags();
  const [newName, setNewName] = useState("");
  const inval = () => {
    qc.invalidateQueries({ queryKey: ["conv_tags_admin"] });
    qc.invalidateQueries({ queryKey: ["conv_tags"] });
  };
  const save = useMutation({
    mutationFn: async (v: Partial<AdminTag> & { id: string }) => {
      const { id, ...patch } = v;
      const { error } = await supabase.from("conversation_tag").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: inval,
  });
  const create = useMutation({
    mutationFn: async () => {
      const name = newName.trim();
      if (!name) return;
      const max = Math.max(0, ...(data ?? []).map((t) => t.sort));
      const { error } = await supabase.from("conversation_tag").insert({
        name,
        color: TAG_COLOR_KEYS[(data?.length ?? 0) % TAG_COLOR_KEYS.length],
        sort: max + 10,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setNewName("");
      inval();
    },
  });

  if (isLoading) return <div className="h-64 animate-pulse rounded-xl bg-slate-100" />;
  const tags = data ?? [];

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-hairline bg-white p-5 shadow-e1">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <TagIcon className="h-4 w-4 text-brand-600" /> Inbox Tags
        </h3>
        <ul className="mt-4 divide-y divide-slate-100">
          {tags.map((t) => (
            <TagRow key={t.id} tag={t} canManage={canManage} onSave={(patch) => save.mutate({ id: t.id, ...patch })} />
          ))}
          {tags.length === 0 && <li className="py-6 text-center text-xs text-slate-400">No tags yet.</li>}
        </ul>
        {canManage && (
          <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  create.mutate();
                }
              }}
              placeholder="New tag name…"
              className="w-48 rounded-md border border-hairline px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none"
            />
            <button
              type="button"
              onClick={() => create.mutate()}
              disabled={!newName.trim() || create.isPending}
              className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-800 disabled:opacity-40"
            >
              <Plus className="h-3.5 w-3.5" /> Add tag
            </button>
          </div>
        )}
        {!canManage && (
          <p className="mt-3 border-t border-slate-100 pt-3 text-[11px] text-slate-400">
            Read-only — editing tags needs the "Manage app settings" capability.
          </p>
        )}
      </div>
      <AutoArchiveCard canManage={canManage} />
    </div>
  );
}

/**
 * INBOX-REOPEN-01 (Seth 2026-09-02): a Done conversation reopens by itself
 * when the customer writes again — except senders on this list, whose
 * conversations always read Done (operational mail the on-call parser
 * consumes, never a customer reply). Table: inbox_auto_archive.
 */
interface AutoArchiveRow { endpoint: string; note: string | null; created_at: string }

function AutoArchiveCard({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const [endpoint, setEndpoint] = useState("");
  const [note, setNote] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["inbox_auto_archive"],
    queryFn: async (): Promise<AutoArchiveRow[]> => {
      const { data, error } = await supabase.from("inbox_auto_archive").select("*").order("endpoint");
      if (error) throw error;
      return (data ?? []) as AutoArchiveRow[];
    },
  });
  const inval = () => {
    qc.invalidateQueries({ queryKey: ["inbox_auto_archive"] });
    qc.invalidateQueries({ queryKey: ["inbox_conversations"] });
  };
  const add = useMutation({
    mutationFn: async () => {
      const ep = endpoint.trim().toLowerCase();
      if (!ep) return;
      const { error } = await supabase.from("inbox_auto_archive").insert({ endpoint: ep, note: note.trim() || null });
      if (error) throw error;
    },
    onSuccess: () => {
      setEndpoint("");
      setNote("");
      inval();
    },
  });
  const remove = useMutation({
    mutationFn: async (ep: string) => {
      const { error } = await supabase.from("inbox_auto_archive").delete().eq("endpoint", ep);
      if (error) throw error;
    },
    onSuccess: inval,
  });
  const rows = data ?? [];
  return (
    <div className="rounded-xl border border-hairline bg-white p-5 shadow-e1">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
        <MailX className="h-4 w-4 text-brand-600" /> Auto-Archive Senders
        <InfoTip label="How auto-archive works">
          A conversation marked Done reopens when the customer writes again. Conversations from these senders never reopen, so
          use it for automated mail like the answering service's morning sheet.
        </InfoTip>
      </h3>
      {isLoading ? (
        <div className="mt-4 h-16 animate-pulse rounded-lg bg-slate-100" />
      ) : (
        <ul className="mt-4 divide-y divide-slate-100">
          {rows.map((r) => (
            <li key={r.endpoint} className="flex items-center gap-3 py-2.5">
              <span className="font-mono text-[13px] font-medium text-slate-800">{r.endpoint}</span>
              {r.note && <span className="min-w-0 flex-1 truncate text-xs text-slate-500">{r.note}</span>}
              {canManage && (
                <button
                  type="button"
                  onClick={() => remove.mutate(r.endpoint)}
                  title="Remove from the list"
                  className="ml-auto inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2 py-1 text-[11px] font-medium text-slate-600 transition-colors hover:bg-slate-50"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </li>
          ))}
          {rows.length === 0 && <li className="py-6 text-center text-xs text-slate-400">No senders listed.</li>}
        </ul>
      )}
      {canManage && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
          <input
            value={endpoint}
            onChange={(e) => setEndpoint(e.target.value)}
            placeholder="Email address or phone number"
            className="w-64 rounded-md border border-hairline px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none"
          />
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                add.mutate();
              }
            }}
            placeholder="Why (optional)"
            className="w-64 rounded-md border border-hairline px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none"
          />
          <button
            type="button"
            onClick={() => add.mutate()}
            disabled={!endpoint.trim() || add.isPending}
            className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-800 disabled:opacity-40"
          >
            <Plus className="h-3.5 w-3.5" /> Add sender
          </button>
        </div>
      )}
    </div>
  );
}

function TagRow({
  tag,
  canManage,
  onSave,
}: {
  tag: AdminTag;
  canManage: boolean;
  onSave: (patch: Partial<AdminTag>) => void;
}) {
  const [name, setName] = useState(tag.name);
  return (
    <li className={cn("flex flex-wrap items-center gap-3 py-2.5", tag.archived && "opacity-50")}>
      <div className="flex items-center gap-1">
        {TAG_COLOR_KEYS.map((k) => {
          const on = tag.color === k;
          return (
            <button
              key={k}
              type="button"
              disabled={!canManage}
              onClick={() => onSave({ color: k })}
              title={k}
              className={cn(
                "h-4 w-4 rounded-full transition",
                colorOf(k).dot,
                on ? "ring-2 ring-slate-500 ring-offset-1" : "opacity-50 hover:opacity-100",
              )}
            />
          );
        })}
      </div>
      <input
        value={name}
        disabled={!canManage}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => {
          if (name.trim() && name.trim() !== tag.name) onSave({ name: name.trim() });
          else setName(tag.name);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
        className="min-w-0 flex-1 rounded-md border border-transparent px-2 py-1 text-sm font-medium text-slate-800 transition-colors hover:border-hairline focus:border-brand-400 focus:outline-none"
      />
      <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", colorOf(tag.color).chip)}>{name || tag.name}</span>
      {canManage && (
        <button
          type="button"
          onClick={() => onSave({ archived: !tag.archived })}
          title={tag.archived ? "Restore" : "Archive"}
          className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2 py-1 text-[11px] font-medium text-slate-600 transition-colors hover:bg-slate-50"
        >
          {tag.archived ? <ArchiveRestore className="h-3 w-3" /> : <Archive className="h-3 w-3" />}
        </button>
      )}
    </li>
  );
}
